import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';
import { ReservationService } from '@app/reservation/reservation.service';
import { TranslateService } from '@ngx-translate/core';
import { Constants, HOTEL_SYSTEM_CONFIG } from '@app/core/constants';
import { ConfirmModalComponent } from '@app/shared/confirm-modal/confirm-modal.component';
import { PmsProductModalComponent } from './edit-pms-product-modal/pms-product-modal.component';
@Component({
  selector: 'app-pms-integration',
  templateUrl: './pms-integration.component.html',
  styleUrls: ['./pms-integration.component.scss']
})
export class PmsIntegrationComponent implements OnInit, OnDestroy {
  HOTEL_SYSTEM_FIELDS: { [system: string]: any[] } = {};

  // Dropdown options are built from HOTEL_SYSTEM_CONFIG keys.
  hotelSystems = Object.keys(HOTEL_SYSTEM_CONFIG).map(system => ({
    id: system, // e.g. "Oracle", "ASA", etc.
    value: system // Display text is the same.
  }));

  dynamicForm: FormGroup;
  selectedHotelSystem: string = '';
  dropdownSelected: string | null = null;

  // durations in minutes
  stayTimes = [
    { label: `1 ${this.translate.instant('HotelReservations.Hour')}`, value: 60 },
    { label: `1.5 ${this.translate.instant('HotelReservations.Hours')}`, value: 90 },
    { label: `2 ${this.translate.instant('HotelReservations.Hours')}`, value: 120 },
    { label: `2.5 ${this.translate.instant('HotelReservations.Hours')}`, value: 150 },
    { label: `4 ${this.translate.instant('HotelReservations.Hours')}`, value: 240 }
  ];

  mealTimeOptions: any = [];

  integrationData: any[] = [];
  productIntegrationData: any[] = [];
  productIntegrationSettingData: any[] = [];
  systemIntegrations: { system: string; data: any[] }[] = [];
  loading: boolean = false;
  clientId: string;
  mealOptions = [
    { value: 'FR', label: this.translate.instant('HotelReservations.Breakfast'), color: 'lightsalmon' },
    { value: 'HP', label: this.translate.instant('HotelReservations.HalfBoard'), color: 'lightpink' },
    { value: 'VP', label: this.translate.instant('HotelReservations.FullBoard'), color: 'lightblue' }
  ];

  mealLabelMap: any[] = [];

  private destroy$ = new Subject<void>();
  pmsProductTypes: any = Constants.pmsProductTypes;

  syncStatus: any = null;
  testingConnection = false;
  syncing = false;
  importing = false;
  connectionEnabled = true;

  constructor(
    private fb: FormBuilder,
    private snackBar: MatSnackBar,
    private reservationService: ReservationService,
    private route: ActivatedRoute,
    private modalService: NgbModal,
    private translate: TranslateService
  ) {}

  ngOnInit() {
    this.clientId = this.route.snapshot.paramMap.get('clientId');
    for (let hour = 0; hour < 24; hour++) {
      for (let minute of [0, 30]) {
        const h = hour.toString().padStart(2, '0');
        const m = minute.toString().padStart(2, '0');
        const time = `${h}:${m}`;
        this.mealTimeOptions.push({ label: time, value: time });
      }
    }

    this.buildHotelSystemFields();
    this.loadInitialData();
    this.getPMSProductData();
  }

  private buildHotelSystemFields() {
    // Default validators
    const defaultValidators = [Validators.required];

    // Build dynamic fields from HOTEL_SYSTEM_CONFIG
    Object.keys(HOTEL_SYSTEM_CONFIG).forEach(system => {
      this.HOTEL_SYSTEM_FIELDS[system] = HOTEL_SYSTEM_CONFIG[system].map(field => ({
        ...field,
        type: field.type || 'text',
        validators: field.validators ? field.validators : defaultValidators
      }));
    });
  }

  loadSyncStatus() {
    this.reservationService
      .getPMSSyncStatus(this.clientId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (settings: any) => {
          if (settings && settings.id) {
            this.syncStatus = settings;
            this.connectionEnabled = settings.enabled !== 0 && settings.enabled !== false;
          }
        },
        () => {}
      );
  }

  testConnection() {
    this.testingConnection = true;
    this.reservationService
      .validatePMSConnection(this.clientId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (res: any) => {
          this.testingConnection = false;
          this.snackBar.open(`Connected: ${res.enterpriseName}`, '', {
            duration: 3000,
            panelClass: ['snackbar-success']
          });
        },
        (err: any) => {
          this.testingConnection = false;
          const msg =
            err.status === 401
              ? 'Invalid credentials — check your AccessToken'
              : err.status === 429
              ? 'MEWS rate limit reached — try again shortly'
              : (err.error && err.error.error) || 'Connection failed';
          this.snackBar.open(msg, '', { duration: 5000, panelClass: ['snackbar-error'] });
        }
      );
  }

  syncNow() {
    this.syncing = true;
    this.reservationService
      .triggerManualSync(this.clientId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (res: any) => {
          this.syncing = false;
          this.snackBar.open(`Sync done: ${res.upserted} updated, ${res.cancelled} cancelled`, '', { duration: 3000 });
          this.loadSyncStatus();
        },
        (err: any) => {
          this.syncing = false;
          this.snackBar.open((err.error && err.error.error) || 'Sync failed', '', {
            duration: 4000,
            panelClass: ['snackbar-error']
          });
          this.loadSyncStatus();
        }
      );
  }

  reimportAll() {
    this.importing = true;
    this.reservationService
      .triggerInitialImport(this.clientId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        () => {
          this.importing = false;
          this.snackBar.open('Re-import started in background', '', { duration: 3000 });
        },
        (err: any) => {
          this.importing = false;
          this.snackBar.open((err.error && err.error.error) || 'Import failed', '', {
            duration: 4000,
            panelClass: ['snackbar-error']
          });
        }
      );
  }

  toggleConnection() {
    const newState = !this.connectionEnabled;
    this.reservationService
      .togglePMSConnection({
        clientId: this.clientId,
        enabled: newState
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        () => {
          this.connectionEnabled = newState;
          this.snackBar.open(`Integration ${newState ? 'enabled' : 'disabled'}`, '', { duration: 2000 });
        },
        () => {
          this.snackBar.open('Failed to update connection status', '', {
            duration: 3000,
            panelClass: ['snackbar-error']
          });
        }
      );
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadInitialData() {
    this.loading = true;
    this.reservationService
      .getPMSData()
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (data: any[]) => {
          this.integrationData = data;
          this.processIntegrationData();
          this.loading = false;
          // this.showAutoPlacement = data.some(row => row.hotelSystem === Constants.hotelIntegrations.ASA);
        },
        error => {
          this.loading = false;
          this.snackBar.open(this.translate.instant('PMSIntegration.SomethingWentWrong'), 'Close', { duration: 2000 });
        }
      );
  }

  getPMSProductData() {
    this.loading = true;
    this.mealLabelMap = this.mealOptions.reduce((acc: any, item) => {
      acc[item.value] = item.label;
      return acc;
    }, {});

    this.reservationService
      .getPMSProductData()
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (resp: any) => {
          if (resp.success) {
            this.productIntegrationData = resp['data'];
          } else {
            this.productIntegrationData = [];
          }
          this.loading = false;
        },
        error => {
          this.loading = false;
          this.snackBar.open(this.translate.instant('PMSProductIntegration.SomethingWentWrong'), 'Close', {
            duration: 2000
          });
        }
      );
  }

  processIntegrationData() {
    const groupedIntegrations = this.integrationData.reduce((acc, item) => {
      const sys = item.hotelSystem; // Expected to match exactly as in HOTEL_SYSTEM_CONFIG
      if (!acc[sys]) {
        acc[sys] = [];
      }
      acc[sys].push(item);
      return acc;
    }, {} as Record<string, any[]>);

    this.systemIntegrations = Object.entries(groupedIntegrations).map(([system, data]) => ({
      system,
      data: data as any[]
    }));

    // Auto-select if exactly one system has data.
    if (this.systemIntegrations.length === 1) {
      const singleSystem = this.systemIntegrations[0].system;
      this.dropdownSelected = singleSystem;
      this.selectHotelSystem(singleSystem);
    } else {
      this.dropdownSelected = null;
      this.selectedHotelSystem = '';
      this.dynamicForm = null;
    }
  }

  onDropdownChange(event: any) {
    if (!event || !event.value) {
      this.selectedHotelSystem = '';
      this.dynamicForm = null;
      return;
    }
    const systemName = event.value;
    this.selectHotelSystem(systemName);
  }

  selectHotelSystem(system: string) {
    if (!HOTEL_SYSTEM_CONFIG[system]) {
      this.snackBar.open(this.translate.instant('PMSIntegration.SomethingWentWrong'), 'Close', { duration: 2000 });
      return;
    }
    this.selectedHotelSystem = system;
    this.dropdownSelected = system;
    this.createDynamicForm(system);
    if (system === 'MEWS') {
      this.loadSyncStatus();
    }
  }

  createDynamicForm(system: string) {
    const formConfig: any = {};
    const fields = this.HOTEL_SYSTEM_FIELDS[system] || [];
    fields.forEach((field: any) => {
      const match = this.integrationData.find(item => item.hotelSystem === system && item.key === field.key);
      formConfig[field.key] = [match ? match.value : '', field.validators];
    });
    this.dynamicForm = this.fb.group(formConfig);
  }

  submitData() {
    if (!this.selectedHotelSystem) {
      this.snackBar.open(this.translate.instant('PMSIntegration.SelectHotelIntegrations'), 'Close', { duration: 2000 });
      return;
    }
    if (!this.dynamicForm || this.dynamicForm.invalid) {
      this.snackBar.open(this.translate.instant('PMSIntegration.SomethingWentWrong'), 'Close', { duration: 2000 });
      return;
    }
    const formData = this.dynamicForm.value;
    const resultArray = Object.entries(formData).map(([key, value]) => {
      const existing = this.integrationData.find(
        item => item.hotelSystem === this.selectedHotelSystem && item.key === key
      );
      return {
        id: existing ? existing.id : null,
        hotelSystem: this.selectedHotelSystem,
        key,
        clientId: this.clientId,
        value
      };
    });
    this.loading = true;
    this.reservationService
      .createPMSData({
        data: resultArray,
        clientId: this.clientId,
        type: this.selectedHotelSystem
      })
      .subscribe(
        () => {
          this.loading = false;
          this.snackBar.open(this.translate.instant('PMSIntegration.Success'), 'Close', { duration: 2000 });
          this.loadInitialData();
        },
        error => {
          this.loading = false;
          this.snackBar.open(this.translate.instant('PMSIntegration.SomethingWentWrong'), 'Close', { duration: 2000 });
          console.error(error);
        }
      );
  }

  hasExistingData(): boolean {
    return this.integrationData.some(item => item.hotelSystem === this.selectedHotelSystem);
  }

  deleteData() {
    if (!this.hasExistingData()) {
      return;
    }
    const modalRef = this.modalService.open(ConfirmModalComponent);
    modalRef.componentInstance.title = this.translate.instant('PMSIntegration.DeleteIntegration');
    modalRef.componentInstance.message = this.translate.instant('PMSIntegration.AreYouSureDelete'); // Create key accordingly
    modalRef.componentInstance.showInfo = false;
    modalRef.componentInstance.buttonText = this.translate.instant('PMSIntegration.Yes');

    modalRef.result.then(
      result => {
        if (result === 'ok') {
          this.loading = true;
          this.reservationService
            .deletPMSData({
              clientId: this.clientId,
              type: this.selectedHotelSystem
            })
            .subscribe(() => {
              this.loading = false;
              this.loadInitialData();
              if (this.dynamicForm) {
                this.dynamicForm.reset();
              }
              this.snackBar.open(this.translate.instant('PMSIntegration.Success'), 'Close', { duration: 2000 });
            });
        }
      },
      () => {
        this.loading = false;
      }
    );
  }

  onProductFormSubmit() {
    const modalRef = this.modalService.open(PmsProductModalComponent);
    modalRef.componentInstance.title = this.translate.instant('PMSProductIntegration.AddProduct');
    modalRef.componentInstance.showInfo = false;
    modalRef.componentInstance.buttonText = this.translate.instant('PMSProductIntegration.Add');

    modalRef.result.then(
      (result: any) => {
        console.log(result);
        if (result && result.success) {
          this.loading = true;
          const {
            data: { codeName, mealName, type }
          } = result;
          this.reservationService
            .createPMSProductData({
              codeName,
              mealName,
              clientId: +this.clientId,
              type
            })
            .subscribe(
              () => {
                this.loading = false;
                this.snackBar.open(this.translate.instant('PMSProductIntegration.SaveIntegration'), 'Close', {
                  duration: 2000
                });
                this.getPMSProductData();
              },
              error => {
                this.loading = false;
                console.log(error);
                this.snackBar.open(
                  error.error.msg || this.translate.instant('PMSProductIntegration.SomethingWentWrong'),
                  'Close',
                  { duration: 2000 }
                );
                console.error(error);
              }
            );
        }
      },
      () => {
        this.loading = false;
      }
    );
  }

  editPMSProductData(data: any) {
    const modalRef = this.modalService.open(PmsProductModalComponent);
    modalRef.componentInstance.title = this.translate.instant('PMSProductIntegration.UpdateHotelIntegrations');
    modalRef.componentInstance.codeName = data.codeName;
    modalRef.componentInstance.mealName = data.mealName;
    modalRef.componentInstance.type = data.type;
    modalRef.componentInstance.showInfo = false;
    modalRef.componentInstance.buttonText = this.translate.instant('PMSProductIntegration.Update');

    modalRef.result.then(
      (result: any) => {
        if (result && result.success) {
          this.loading = true;
          const {
            data: { codeName, mealName, type }
          } = result;
          this.reservationService
            .createPMSProductData({
              codeName: codeName,
              mealName: mealName,
              clientId: +this.clientId,
              id: data.id,
              type: type
            })
            .subscribe(
              () => {
                this.loading = false;
                this.snackBar.open(this.translate.instant('PMSProductIntegration.UpdateHotelIntegrations'), 'Close', {
                  duration: 2000
                });
                this.getPMSProductData();
              },
              error => {
                this.loading = false;
                this.snackBar.open(this.translate.instant('PMSProductIntegration.SomethingWentWrong'), 'Close', {
                  duration: 2000
                });
                console.error(error);
              }
            );
        }
      },
      () => {
        this.loading = false;
      }
    );
  }

  deletePMSProductData(id: number) {
    const modalRef = this.modalService.open(ConfirmModalComponent);
    modalRef.componentInstance.title = this.translate.instant('PMSProductIntegration.DeleteIntegration');
    modalRef.componentInstance.message = this.translate.instant('PMSProductIntegration.AreYouSureDelete'); // Create key accordingly
    modalRef.componentInstance.showInfo = false;
    modalRef.componentInstance.buttonText = this.translate.instant('PMSProductIntegration.Yes');

    modalRef.result.then(
      result => {
        if (result === 'ok') {
          this.loading = true;
          this.reservationService
            .deletPMSProductData({
              id
            })
            .subscribe((resp: any) => {
              console.log(resp);
              this.loading = false;
              if (resp.success) {
                this.getPMSProductData();
                this.snackBar.open(this.translate.instant('PMSProductIntegration.Success'), 'Close', {
                  duration: 2000
                });
              } else {
                this.snackBar.open(this.translate.instant('PMSProductIntegration.SomethingWentWrong'), 'Close', {
                  duration: 2000
                });
              }
            });
        }
      },
      () => {
        this.loading = false;
      }
    );
  }

  getBackgroundColor(mealName: any) {
    return this.mealOptions.find(x => x.value === mealName).color || '';
  }

  getProductTypeLabel(value: string): string {
    const match = this.pmsProductTypes.find((x: any) => x.value === value);
    return match ? match.label : 'NA';
  }
}
